import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCarCrash, faCogs, faCheck } from '@fortawesome/free-solid-svg-icons';
import { StepProps, DamageType } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary mb-6`}
`;

const OptionsContainer = styled.div`
  ${tw`flex flex-col gap-4 mb-8`}
`;

const OptionCard = styled.button<{ isSelected: boolean }>`
  ${tw`w-full p-6 text-left border-2 rounded-card transition-all flex items-start gap-4`}
  ${props =>
    props.isSelected
      ? tw`border-black bg-pink-50`
      : tw`border-border-default hover:border-border-default`}
`;

const IconWrapper = styled.div<{ isSelected: boolean }>`
  ${tw`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center`}
  ${props => (props.isSelected ? tw`bg-pink-100` : tw`bg-bg-secondary`)}
`;

const Icon = styled(FontAwesomeIcon)<{ isSelected: boolean }>`
  ${props => (props.isSelected ? tw`text-black` : tw`text-text-secondary`)}
`;

const CardContent = styled.div`
  ${tw`flex-1`}
`;

const CardTitle = styled.h3`
  ${tw`font-semibold text-text-primary mb-1`}
`;

const CardDescription = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

const CheckIcon = styled.div<{ isSelected: boolean }>`
  ${tw`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center`}
  ${props => (props.isSelected ? tw`bg-black` : tw`bg-bg-secondary`)}
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

const DamageTypeStep: React.FC<StepProps> = ({ formData, updateFormData, onNext }) => {
  const handleSelect = (type: DamageType) => {
    updateFormData({ damageType: type, physicalDamageSubcategory: undefined });
  };

  const handleContinue = () => {
    if (formData.damageType) {
      onNext();
    }
  };

  return (
    <Container>
      <Title>Select damage type</Title>

      <OptionsContainer>
        <OptionCard
          isSelected={formData.damageType === DamageType.PHYSICAL}
          onClick={() => handleSelect(DamageType.PHYSICAL)}
        >
          <IconWrapper isSelected={formData.damageType === DamageType.PHYSICAL}>
            <Icon
              icon={faCarCrash}
              size="lg"
              isSelected={formData.damageType === DamageType.PHYSICAL}
            />
          </IconWrapper>
          <CardContent>
            <CardTitle>Physical damage</CardTitle>
            <CardDescription>
              Any damage to the vehicle's keys, tires, interior or exterior that wasn't there before
              the trip started.
            </CardDescription>
          </CardContent>
          <CheckIcon isSelected={formData.damageType === DamageType.PHYSICAL}>
            {formData.damageType === DamageType.PHYSICAL && (
              <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
            )}
          </CheckIcon>
        </OptionCard>

        <OptionCard
          isSelected={formData.damageType === DamageType.MECHANICAL}
          onClick={() => handleSelect(DamageType.MECHANICAL)}
        >
          <IconWrapper isSelected={formData.damageType === DamageType.MECHANICAL}>
            <Icon
              icon={faCogs}
              size="lg"
              isSelected={formData.damageType === DamageType.MECHANICAL}
            />
          </IconWrapper>
          <CardContent>
            <CardTitle>Mechanical damage</CardTitle>
            <CardDescription>
              Problems with the engine, clutch, battery, brakes, or other mechanical/electrical
              components that prevent normal operation.
            </CardDescription>
          </CardContent>
          <CheckIcon isSelected={formData.damageType === DamageType.MECHANICAL}>
            {formData.damageType === DamageType.MECHANICAL && (
              <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
            )}
          </CheckIcon>
        </OptionCard>
      </OptionsContainer>

      <ContinueButton disabled={!formData.damageType} onClick={handleContinue}>
        Continue
      </ContinueButton>
    </Container>
  );
};

export default DamageTypeStep;
